import { useState, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, Bot, Check, X, AlertTriangle, FileText, Sparkles, Loader2,
  ChevronDown, ChevronUp, Pencil, Brain, Zap, Building2,
  CreditCard, Receipt, Landmark, IndianRupee, BarChart3, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { extractInvoiceData, ExtractedInvoiceData } from "@/lib/ai/invoiceExtractor";
import { processBookkeepingDocument, BankTransaction, BookkeepingResult, BookkeepingDocType } from "@/lib/ai/bookkeepingExtractor";
import { cn } from "@/lib/utils";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const fmt = (n: number) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;
const CHART_COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

// ---- Badge Components ----
function ConfidenceBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 85 ? "bg-emerald-500/10 text-emerald-600" : pct >= 65 ? "bg-amber-500/10 text-amber-600" : "bg-destructive/10 text-destructive";
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${color}`}>{pct}%</span>;
}

function DocTypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    "bank-statement": "bg-primary/10 text-primary",
    invoice: "bg-chart-2/10 text-chart-2",
    receipt: "bg-chart-3/10 text-chart-3",
    "payment-record": "bg-chart-4/10 text-chart-4",
  };
  const labels: Record<string, string> = {
    "bank-statement": "Bank Statement", invoice: "Invoice", receipt: "Receipt", "payment-record": "Payment",
  };
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${styles[type] || "bg-muted text-muted-foreground"}`}>{labels[type] || type}</span>;
}

// ---- Transaction Row with expand/edit ----
function TransactionRow({
  tx, onApprove, onReject, onModify,
}: {
  tx: BankTransaction;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onModify: (id: string, updates: Partial<BankTransaction>) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    partyName: tx.partyName || "",
    amount: tx.amount,
    category: tx.category,
    suggestedDebitLedger: tx.suggestedDebitLedger,
    suggestedCreditLedger: tx.suggestedCreditLedger,
    narration: tx.narration,
  });

  const saveEdit = () => {
    onModify(tx.id, { ...editData, partyName: editData.partyName, amount: editData.amount, status: "modified" });
    setEditing(false);
  };

  return (
    <>
      <TableRow className={cn("cursor-pointer transition-colors", tx.status === "approved" && "bg-emerald-500/5", tx.status === "rejected" && "opacity-50")} onClick={() => setExpanded(!expanded)}>
        <TableCell className="text-xs">{tx.date}</TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium">{tx.partyName || tx.description.slice(0, 30)}</span>
            <ConfidenceBadge value={tx.confidence} />
          </div>
          <p className="text-xs text-muted-foreground truncate max-w-[250px]">{tx.description}</p>
        </TableCell>
        <TableCell><Badge variant="outline" className="text-xs">{tx.category}</Badge></TableCell>
        <TableCell className={cn("text-right font-medium", tx.type === "credit" ? "text-emerald-600" : "text-foreground")}>
          {tx.type === "credit" ? "+" : "-"}{fmt(tx.amount)}
        </TableCell>
        <TableCell className="text-xs">
          <div className="text-emerald-600">Dr: {tx.suggestedDebitLedger}</div>
          <div className="text-destructive">Cr: {tx.suggestedCreditLedger}</div>
        </TableCell>
        <TableCell>
          {tx.status === "pending" || tx.status === "modified" ? (
            <Badge variant="outline" className="text-xs">{tx.status === "modified" ? "Modified" : "Pending"}</Badge>
          ) : tx.status === "approved" ? (
            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs">Approved</Badge>
          ) : (
            <Badge variant="destructive" className="text-xs">Rejected</Badge>
          )}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          {(tx.status === "pending" || tx.status === "modified") && (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={() => onApprove(tx.id)}><Check className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(true); setExpanded(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => onReject(tx.id)}><X className="h-3.5 w-3.5" /></Button>
            </div>
          )}
        </TableCell>
      </TableRow>
      <AnimatePresence>
        {expanded && (
          <TableRow>
            <TableCell colSpan={7} className="p-0">
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="p-4 bg-muted/20 space-y-3">
                  {/* Journal entry preview */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
                      <span className="text-[10px] text-muted-foreground uppercase">DEBIT</span>
                      <p className="font-semibold text-sm">{tx.suggestedDebitLedger}</p>
                      <p className="text-xs text-muted-foreground">{tx.suggestedDebitGroup}</p>
                      <p className="font-bold text-emerald-600 mt-1">{fmt(tx.amount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/15">
                      <span className="text-[10px] text-muted-foreground uppercase">CREDIT</span>
                      <p className="font-semibold text-sm">{tx.suggestedCreditLedger}</p>
                      <p className="text-xs text-muted-foreground">{tx.suggestedCreditGroup}</p>
                      <p className="font-bold text-destructive mt-1">{fmt(tx.amount)}</p>
                    </div>
                  </div>
                  {tx.gstEstimate && tx.gstEstimate > 0 && (
                    <p className="text-xs text-muted-foreground">Estimated GST: {fmt(tx.gstEstimate)}</p>
                  )}
                  <p className="text-xs"><span className="text-muted-foreground">Narration:</span> {tx.narration}</p>
                  <p className="text-xs text-muted-foreground">Source: {tx.fileName}</p>

                  {editing && (
                    <div className="p-3 rounded-lg border border-accent/20 bg-accent/5 space-y-3">
                      <p className="text-xs font-semibold text-accent flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit Entry</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div><label className="text-[10px] text-muted-foreground">Party</label><Input value={editData.partyName} onChange={(e) => setEditData({ ...editData, partyName: e.target.value })} className="h-8 text-xs" /></div>
                        <div><label className="text-[10px] text-muted-foreground">Amount</label><Input type="number" value={editData.amount} onChange={(e) => setEditData({ ...editData, amount: +e.target.value })} className="h-8 text-xs" /></div>
                        <div><label className="text-[10px] text-muted-foreground">Category</label><Input value={editData.category} onChange={(e) => setEditData({ ...editData, category: e.target.value })} className="h-8 text-xs" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-[10px] text-muted-foreground">Debit Ledger</label><Input value={editData.suggestedDebitLedger} onChange={(e) => setEditData({ ...editData, suggestedDebitLedger: e.target.value })} className="h-8 text-xs" /></div>
                        <div><label className="text-[10px] text-muted-foreground">Credit Ledger</label><Input value={editData.suggestedCreditLedger} onChange={(e) => setEditData({ ...editData, suggestedCreditLedger: e.target.value })} className="h-8 text-xs" /></div>
                      </div>
                      <div><label className="text-[10px] text-muted-foreground">Narration</label><Textarea value={editData.narration} onChange={(e) => setEditData({ ...editData, narration: e.target.value })} className="text-xs min-h-[50px]" /></div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEdit} className="h-7 text-xs"><Check className="h-3 w-3 mr-1" /> Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs">Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </TableCell>
          </TableRow>
        )}
      </AnimatePresence>
    </>
  );
}

// ---- Main Page ----
const AIAutoEntryPage = () => {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingFiles, setProcessingFiles] = useState<string[]>([]);

  // All entries from all sources
  const [allTransactions, setAllTransactions] = useState<BankTransaction[]>([]);
  const [invoiceSuggestions, setInvoiceSuggestions] = useState<ExtractedInvoiceData[]>([]);
  const [bankResults, setBankResults] = useState<BookkeepingResult[]>([]);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterDocType, setFilterDocType] = useState<string>("all");
  const [detailTx, setDetailTx] = useState<BankTransaction | null>(null);

  const processFiles = useCallback(async (files: File[]) => {
    setProcessing(true);
    setProcessingFiles(files.map((f) => f.name));

    for (const file of files) {
      try {
        const name = file.name.toLowerCase();
        const isInvoice = name.includes("invoice") || name.includes("inv") || (file.type.includes("pdf") && !name.includes("statement") && !name.includes("bank"));
        const isBankCSV = name.includes("statement") || name.includes("bank") || file.type === "text/csv" || name.endsWith(".csv");

        if (isInvoice && !isBankCSV) {
          // Use existing invoice extractor
          const extracted = await extractInvoiceData(file);
          setInvoiceSuggestions((prev) => [extracted, ...prev]);
        } else {
          // Use new bookkeeping extractor
          const result = await processBookkeepingDocument(file);
          if (result.transactions.length > 0) {
            setBankResults((prev) => [result, ...prev]);
            setAllTransactions((prev) => [...result.transactions, ...prev]);
          }
        }
        setProcessingFiles((prev) => prev.filter((n) => n !== file.name));
      } catch {
        toast({ title: "Processing failed", description: `Could not process ${file.name}`, variant: "destructive" });
        setProcessingFiles((prev) => prev.filter((n) => n !== file.name));
      }
    }

    setProcessing(false);
    toast({ title: `${files.length} file${files.length > 1 ? "s" : ""} processed`, description: "AI has generated draft entries for review." });
  }, [toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    if (e.dataTransfer.files.length) processFiles(Array.from(e.dataTransfer.files));
  }, [processFiles]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) processFiles(Array.from(e.target.files));
    e.target.value = "";
  }, [processFiles]);

  // Transaction actions
  const approveTx = useCallback((id: string) => {
    setAllTransactions((prev) => prev.map((t) => t.id === id ? { ...t, status: "approved" as const } : t));
    toast({ title: "Entry approved & created" });
  }, [toast]);

  const rejectTx = useCallback((id: string) => {
    setAllTransactions((prev) => prev.map((t) => t.id === id ? { ...t, status: "rejected" as const } : t));
  }, []);

  const modifyTx = useCallback((id: string, updates: Partial<BankTransaction>) => {
    setAllTransactions((prev) => prev.map((t) => t.id === id ? { ...t, ...updates } : t));
    toast({ title: "Entry modified" });
  }, [toast]);

  // Invoice actions
  const approveInvoice = useCallback((id: string) => {
    setInvoiceSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, status: "approved" as const, reviewNotes: `Created at ${new Date().toLocaleTimeString()}` } : s));
    toast({ title: "Invoice entry approved" });
  }, [toast]);

  const rejectInvoice = useCallback((id: string) => {
    setInvoiceSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, status: "rejected" as const, reviewNotes: "Rejected" } : s));
  }, []);

  const modifyInvoice = useCallback((id: string, updates: Partial<ExtractedInvoiceData>) => {
    setInvoiceSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, ...updates, status: "modified" as const } : s));
    toast({ title: "Invoice entry modified" });
  }, [toast]);

  // Filtered
  const filteredTx = useMemo(() => {
    let items = allTransactions;
    if (filterCategory !== "all") items = items.filter((t) => t.category === filterCategory);
    return items;
  }, [allTransactions, filterCategory]);

  const pendingTx = filteredTx.filter((t) => t.status === "pending" || t.status === "modified");
  const approvedTx = filteredTx.filter((t) => t.status === "approved");
  const rejectedTx = filteredTx.filter((t) => t.status === "rejected");

  const pendingInv = invoiceSuggestions.filter((s) => s.status === "pending" || s.status === "modified");
  const approvedInv = invoiceSuggestions.filter((s) => s.status === "approved");

  const totalPending = pendingTx.length + pendingInv.length;
  const totalApproved = approvedTx.length + approvedInv.length;
  const totalValue = allTransactions.reduce((s, t) => s + t.amount, 0) + invoiceSuggestions.reduce((s, i) => s + i.totalAmount, 0);

  // Analytics
  const categoryBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    allTransactions.forEach((t) => { map[t.category] = (map[t.category] || 0) + t.amount; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [allTransactions]);

  const categories = useMemo(() => {
    const cats = new Set(allTransactions.map((t) => t.category));
    return Array.from(cats).sort();
  }, [allTransactions]);

  const hasData = allTransactions.length > 0 || invoiceSuggestions.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="h-6 w-6 text-primary" /> AI Auto Bookkeeping
          </h1>
          <p className="text-sm text-muted-foreground">Upload bank statements, invoices, receipts & payment records → AI generates draft entries → Review & approve</p>
        </div>
        <Badge className="bg-primary/10 text-primary border-primary/20 gap-1"><Sparkles className="h-3 w-3" /> AccouPro AI</Badge>
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer flex flex-col items-center gap-3 text-center",
          isDragging ? "border-primary bg-primary/5 scale-[1.01]" : "border-border hover:border-primary/50 hover:bg-muted/30"
        )}
      >
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Upload className="h-7 w-7 text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold">Drop files here for AI processing</p>
          <p className="text-xs text-muted-foreground mt-1">Supports: Bank Statements (CSV/Excel), Invoices (PDF), Receipts (JPG/PNG), Payment Records</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-center">
          {[
            { icon: Building2, label: "Bank Statements" },
            { icon: FileText, label: "Invoices" },
            { icon: Receipt, label: "Receipts" },
            { icon: CreditCard, label: "Payments" },
          ].map((t) => (
            <span key={t.label} className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted px-2 py-1 rounded-full">
              <t.icon className="h-3 w-3" /> {t.label}
            </span>
          ))}
        </div>
        <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx,.docx,.txt" onChange={handleInputChange} className="hidden" />
      </div>

      {/* Processing indicator */}
      <AnimatePresence>
        {processingFiles.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-2">
            {processingFiles.map((name) => (
              <div key={name} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <Loader2 className="h-4 w-4 text-primary animate-spin" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{name}</p>
                  <p className="text-[10px] text-primary">AI analyzing document...</p>
                </div>
                <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                  <motion.div className="h-full bg-primary rounded-full" initial={{ width: "5%" }} animate={{ width: "90%" }} transition={{ duration: 2.5, ease: "easeInOut" }} />
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {hasData && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
                <div><p className="text-sm text-muted-foreground">Pending Review</p><p className="text-2xl font-bold">{totalPending}</p></div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10"><Check className="h-5 w-5 text-emerald-600" /></div>
                <div><p className="text-sm text-muted-foreground">Approved</p><p className="text-2xl font-bold">{totalApproved}</p></div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10"><IndianRupee className="h-5 w-5 text-primary" /></div>
                <div><p className="text-sm text-muted-foreground">Total Value</p><p className="text-2xl font-bold">{fmt(totalValue)}</p></div>
              </div>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-chart-3/10"><Brain className="h-5 w-5 text-chart-3" /></div>
                <div><p className="text-sm text-muted-foreground">Files Processed</p><p className="text-2xl font-bold">{bankResults.length + invoiceSuggestions.length}</p></div>
              </div>
            </CardContent></Card>
          </div>

          {/* Main Tabs */}
          <Tabs defaultValue="transactions" className="space-y-4">
            <TabsList>
              <TabsTrigger value="transactions"><FileText className="mr-1 h-4 w-4" /> All Entries ({allTransactions.length + invoiceSuggestions.length})</TabsTrigger>
              <TabsTrigger value="bank"><Building2 className="mr-1 h-4 w-4" /> Bank Statements ({allTransactions.length})</TabsTrigger>
              <TabsTrigger value="invoices"><Receipt className="mr-1 h-4 w-4" /> Invoices ({invoiceSuggestions.length})</TabsTrigger>
              <TabsTrigger value="analytics"><BarChart3 className="mr-1 h-4 w-4" /> Analytics</TabsTrigger>
            </TabsList>

            {/* ALL ENTRIES */}
            <TabsContent value="transactions" className="space-y-4">
              <div className="flex gap-3 items-center">
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[200px]"><Filter className="h-3 w-3 mr-1" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                {pendingTx.length > 1 && (
                  <Button size="sm" variant="outline" onClick={() => pendingTx.forEach((t) => approveTx(t.id))} className="gap-1">
                    <Check className="h-3 w-3" /> Approve All ({pendingTx.length})
                  </Button>
                )}
              </div>

              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Party / Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Suggested Entry</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {filteredTx.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No transactions yet. Upload bank statements, receipts, or payment records.</TableCell></TableRow>
                      )}
                      {filteredTx.map((tx) => (
                        <TransactionRow key={tx.id} tx={tx} onApprove={approveTx} onReject={rejectTx} onModify={modifyTx} />
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* BANK STATEMENTS */}
            <TabsContent value="bank" className="space-y-4">
              {bankResults.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No bank statements processed yet</p>
                  <p className="text-sm">Upload CSV or Excel bank statements for AI analysis</p>
                </CardContent></Card>
              ) : (
                bankResults.map((br, idx) => (
                  <Card key={idx}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-primary" />
                            {br.bankName || "Bank Statement"} {br.accountNumber && `(${br.accountNumber})`}
                          </CardTitle>
                          <CardDescription>{br.statementPeriod || br.fileName} · {br.transactions.length} transactions</CardDescription>
                        </div>
                        <Badge variant="outline">{br.transactions.filter((t) => t.status === "pending").length} pending</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Date</TableHead><TableHead>Party / Description</TableHead><TableHead>Category</TableHead>
                          <TableHead className="text-right">Amount</TableHead><TableHead>Entry</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {br.transactions.map((tx) => (
                            <TransactionRow key={tx.id} tx={tx} onApprove={approveTx} onReject={rejectTx} onModify={modifyTx} />
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* INVOICES */}
            <TabsContent value="invoices" className="space-y-4">
              {invoiceSuggestions.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No invoices processed yet</p>
                  <p className="text-sm">Upload invoice PDFs for AI extraction</p>
                </CardContent></Card>
              ) : (
                <div className="space-y-3">
                  {pendingInv.length > 1 && (
                    <Button size="sm" variant="outline" onClick={() => pendingInv.forEach((s) => approveInvoice(s.id))} className="gap-1">
                      <Check className="h-3 w-3" /> Approve All ({pendingInv.length})
                    </Button>
                  )}
                  {invoiceSuggestions.map((inv) => (
                    <Card key={inv.id} className={cn(
                      "transition-all",
                      inv.status === "approved" && "border-emerald-500/30",
                      inv.status === "rejected" && "opacity-50",
                    )}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold">{inv.vendor}</span>
                              <ConfidenceBadge value={inv.confidence} />
                              {inv.aiPowered && <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px] gap-0.5"><Sparkles className="h-2.5 w-2.5" /> AI</Badge>}
                              <Badge variant={inv.status === "approved" ? "default" : inv.status === "rejected" ? "destructive" : "outline"} className="text-[10px]">{inv.status}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{inv.fileName} · {inv.invoiceNumber} · {inv.invoiceDate}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <div><span className="text-[10px] text-muted-foreground">Amount</span><p className="text-sm font-bold">{fmt(inv.totalAmount)}</p></div>
                              <div><span className="text-[10px] text-muted-foreground">GST ({inv.gstRate}%)</span><p className="text-sm font-semibold">{fmt(inv.totalGST)}</p></div>
                              <div><span className="text-[10px] text-muted-foreground">Category</span><p className="text-sm font-medium text-primary">{inv.category}</p></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-3">
                              <div className="p-2 rounded bg-emerald-500/5 border border-emerald-500/15 text-xs">
                                <span className="text-[10px] text-muted-foreground">DEBIT</span>
                                <p className="font-semibold">{inv.suggestedDebitLedger}</p>
                                <p className="font-bold text-emerald-600">{fmt(inv.totalAmount)}</p>
                              </div>
                              <div className="p-2 rounded bg-destructive/5 border border-destructive/15 text-xs">
                                <span className="text-[10px] text-muted-foreground">CREDIT</span>
                                <p className="font-semibold">{inv.suggestedCreditLedger}</p>
                                <p className="font-bold text-destructive">{fmt(inv.totalAmount)}</p>
                              </div>
                            </div>
                          </div>
                          {(inv.status === "pending" || inv.status === "modified") && (
                            <div className="flex gap-1">
                              <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => approveInvoice(inv.id)}>
                                <Check className="h-3 w-3" /> Approve
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 text-xs text-destructive" onClick={() => rejectInvoice(inv.id)}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ANALYTICS */}
            <TabsContent value="analytics" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Expense Category Breakdown</CardTitle>
                    <CardDescription>AI-classified transaction categories</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {categoryBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={categoryBreakdown} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {categoryBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v: number) => fmt(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center py-12 text-muted-foreground text-sm">Process bank statements to see analytics</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Category-wise Amounts</CardTitle>
                    <CardDescription>Total amounts by AI-detected category</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {categoryBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={categoryBreakdown} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis type="number" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} className="text-xs" />
                          <YAxis type="category" dataKey="name" width={120} className="text-xs" />
                          <Tooltip formatter={(v: number) => fmt(v)} />
                          <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center py-12 text-muted-foreground text-sm">No data yet</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">AI Detection Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { label: "Vendors Detected", value: new Set(allTransactions.map((t) => t.partyName).filter(Boolean)).size, icon: Building2 },
                        { label: "GST Entries", value: allTransactions.filter((t) => t.gstEstimate && t.gstEstimate > 0).length + invoiceSuggestions.filter((i) => i.totalGST > 0).length, icon: Landmark },
                        { label: "Avg Confidence", value: allTransactions.length > 0 ? `${Math.round(allTransactions.reduce((s, t) => s + t.confidence, 0) / allTransactions.length * 100)}%` : "N/A", icon: Brain },
                        { label: "Auto-Categorized", value: allTransactions.length + invoiceSuggestions.length, icon: Sparkles },
                      ].map((s) => (
                        <div key={s.label} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                          <s.icon className="h-5 w-5 text-primary" />
                          <div><p className="text-lg font-bold">{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Empty State */}
      {!hasData && !processing && (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Bot className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-1">AI Auto Bookkeeping</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
            Upload any financial document and AccouPro AI will automatically detect vendors, amounts, GST, categorize transactions, and generate draft accounting entries.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
            {[
              { icon: Building2, title: "Bank Statements", desc: "CSV/Excel auto-parsed" },
              { icon: FileText, title: "Invoices", desc: "PDF vendor extraction" },
              { icon: Receipt, title: "Receipts", desc: "Amount & GST detection" },
              { icon: CreditCard, title: "Payments", desc: "Challan/NEFT parsing" },
            ].map((f) => (
              <div key={f.title} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-muted/30 border border-border/50">
                <f.icon className="h-6 w-6 text-primary" />
                <span className="text-xs font-semibold">{f.title}</span>
                <span className="text-[10px] text-muted-foreground">{f.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAutoEntryPage;
